import { warnOnce } from "@smartsheet/lodestar-utils";
import React, { forwardRef, ReactNode, useMemo } from "react";
import { HTMLAttributes } from "react";
import { ClickAwayListener, Flyout, FlyoutProps } from "../../flyout";
import { useFocusMode } from "../../focusMode";
import { MenuParentContextProvider, useMenuParentContext } from "../context/MenuParentContext";
import { MenuPlacement } from "../types/MenuPlacement";
import { FocusStrategy, MenuContainer, MenuContainerContext, MenuContainerProps } from "./MenuContainer";

const warnNoAriaLabel = warnOnce("An aria-label or aria-labelledby prop is required on the Menu for accessibility.");

type FlyoutPropsForMenu = Omit<FlyoutProps, "arrowColor" | "hasArrow" | "components" | "children">;

interface MenuOwnProps {
    /**
     * The HTML id of the Menu.
     */
    id: string;

    /**
     * When true, this allows click events to propagate out of the Menu. This helps fix errors when
     * the Menu is used within a `ClickAwayListener`. By default, click event propagation was
     * blocked to reduce event noise in parent components. This prop will likely change its default
     * to `true` in a future release.
     */
    allowClickPropagation?: boolean;

    /**
     * Children of the role "menuitem" for this Menu.
     */
    children?: ReactNode;

    /**
     * Specify a set of components to use instead of the defaults.
     *
     * - FlyoutContainer: the root component which receives the popper container attributes and styles.
     *     Note: If you're overriding this, make sure you re-add the logic you lose by that override.
     *     One solution is to re-wrap the existing component (MenuContainer) to keep the logic there
     *     and then shim in your wrapped component here.
     * - Portal: the component to use as the Portal around everything.
     */
    components?: Pick<Required<FlyoutProps>["components"], "FlyoutContainer" | "Portal">;

    /**
     * A flag to tell the Menu which element to focus when after it renders.
     * If you're using `useMenu`, this is managed for you.
     */
    focusStrategy?: FocusStrategy;

    /**
     * If true, will instruct this menu's menuitems to shift to accomodate them.
     * This aligns the text and icons better.
     *
     * If you're creating a custom menuitem, this information is passed into the MenuParentContext.
     *
     * Defaults to false.
     */
    hasContentBefore?: boolean;

    /**
     * If true, we'll add a Blanket component behind the Menu to block click events. This is closer
     * to how operating systems do it and is what UX desires.
     */
    includeBlanket?: boolean;

    /**
     * The way menu items are displayed.
     * Compact stands for a 32px height menu item.
     * Normal stands for a 40px height menu item.
     */
    layout?: "compact" | "normal";

    /**
     * If true, will cause ArrowDown on the last menuitem to focus the first menuitem,
     * and visa-versa for ArrowUp on the first menuitem.
     *
     * Defaults to true.
     */
    loop?: boolean;

    /**
     * Specify a direction and alignment to open the Menu relative to the target element.
     */
    placement?: MenuPlacement;

    /**
     * The HTML role of the Menu.
     */
    role?: string;
}

export type MenuProps = FlyoutPropsForMenu & HTMLAttributes<HTMLDivElement> & MenuOwnProps;

export const Menu = forwardRef<HTMLDivElement, MenuProps>(
    (
        {
            "aria-describedby": ariaDescribedBy,
            "aria-label": ariaLabel,
            "aria-labelledby": ariaLabelledby,
            children,
            clientId,
            components,
            focusStrategy = "none",
            hasContentBefore = false,
            id,
            isOpen,
            loop = true,
            offset,
            closeOnEscape = true,
            onCloseRequested,
            placement = "bottom-start",
            popperProps,
            portalProps,
            role = "menu",
            targetElement,
            updateKey,
            layout = "compact",
            includeBlanket = false,
            allowClickPropagation = false,
            ...rest
        },
        ref
    ) => {
        if (process.env.NODE_ENV !== "production") {
            if (!ariaLabel && !ariaLabelledby) {
                warnNoAriaLabel();
            }
        }

        const { focusMode } = useFocusMode();
        const { parent, closeMenuRootRequested: parentCloseMenuRootRequested } = useMenuParentContext();

        const memoizedMenuContainerProps = useMemo<MenuContainerProps>(() => {
            return {
                clientId,
                focusStrategy,
                loop,
                onCloseRequested,
                parent,
                placement,
                includeBlanket,
            };
        }, [clientId, focusStrategy, loop, onCloseRequested, parent, placement, includeBlanket]);

        return isOpen ? (
            <MenuParentContextProvider
                parent="menu"
                focusMode={focusMode}
                // If the current context value of closeMenuRootRequested is the null, that means
                // there are no parent contexts above us i.e. this will be the root context. If
                // that's the case, pass the onCloseRequested of this Menu on to the lower
                // components so submenus can close the entire menu hierarchy.
                onCloseRequested={parentCloseMenuRootRequested || onCloseRequested}
                hasContentBefore={hasContentBefore}
                layout={layout}
                allowClickPropagation={allowClickPropagation}
            >
                <MenuContainerContext.Provider value={memoizedMenuContainerProps}>
                    <ClickAwayListener onClickAway={onCloseRequested}>
                        <Flyout
                            aria-describedby={ariaDescribedBy}
                            aria-label={ariaLabel}
                            aria-labelledby={ariaLabelledby}
                            arrowColor={undefined}
                            clientId={clientId}
                            closeOnEscape={closeOnEscape}
                            components={{
                                FlyoutContainer: MenuContainer,
                                ...components,
                            }}
                            hasArrow={false}
                            id={id}
                            isOpen={isOpen}
                            offset={offset}
                            onCloseRequested={onCloseRequested}
                            placement={placement}
                            popperProps={popperProps}
                            portalProps={portalProps}
                            role={role}
                            targetElement={targetElement}
                            updateKey={updateKey}
                            ref={ref}
                            {...rest}
                        >
                            {children}
                        </Flyout>
                    </ClickAwayListener>
                </MenuContainerContext.Provider>
            </MenuParentContextProvider>
        ) : null;
    }
);

Menu.displayName = "Menu";
